/**
 * <mkt-infinite> — endless product feed for the marketplace "Recommended" grid.
 *
 * Fetches the next page of the same section through Shopify's Section Rendering
 * API (?section_id=…&page=N) and appends the new cards to the existing grid.
 * Two modes, set via data-mode:
 *   - "infinite": a sentinel below the grid auto-loads as the shopper scrolls.
 *   - "button":   a "Load more" button loads the next page on click.
 * Stops once the last paginated page has been reached (or on a network error).
 */
if (!customElements.get('mkt-infinite')) {
  class MktInfinite extends HTMLElement {
    constructor() {
      super();
      this.grid = this.querySelector('[data-mkt-reco-grid]');
      this.sentinel = this.querySelector('.mkt-infinite__sentinel');
      this.spinner = this.querySelector('.mkt-infinite__spinner');
      this.endEl = this.querySelector('.mkt-infinite__end');
      this.button = this.querySelector('.mkt-loadmore');
      this.mode = this.dataset.mode === 'button' ? 'button' : 'infinite';
      this.sectionId = this.dataset.sectionId;
      this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
      this.startPage = this.currentPage;
      this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
      this.loading = false;
    }

    connectedCallback() {
      if (!this.grid) return;
      if (this.currentPage >= this.totalPages) {
        this.finish();
        return;
      }
      if (this.mode === 'button') {
        if (!this.button) return;
        this.button.addEventListener('click', () => this.loadNext());
        return;
      }
      if (!this.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.loading) this.loadNext();
          });
        },
        { rootMargin: '200px 0px' }
      );
      this.observer.observe(this.sentinel);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async loadNext() {
      if (this.loading || this.currentPage >= this.totalPages) return;
      this.loading = true;
      if (this.spinner) this.spinner.hidden = false;
      if (this.button) this.button.disabled = true;

      const nextPage = this.currentPage + 1;
      const url =
        window.location.pathname +
        '?section_id=' +
        encodeURIComponent(this.sectionId) +
        '&page=' +
        nextPage;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('[data-mkt-reco-grid]');

        if (newGrid && newGrid.children.length) {
          const fragment = document.createDocumentFragment();
          Array.from(newGrid.children).forEach((card) => fragment.appendChild(card));
          this.grid.appendChild(fragment);
          // Nudge lazysizes to reveal freshly-appended images if it's present.
          if (window.lazySizes && window.lazySizes.loader) {
            this.grid.querySelectorAll('.lazyload').forEach((img) => {
              window.lazySizes.loader.unveil(img);
            });
          }
        }

        this.currentPage = nextPage;
      } catch (err) {
        // Give up quietly on failure so the shopper isn't stuck retrying.
        this.currentPage = this.totalPages;
      } finally {
        this.loading = false;
        if (this.spinner) this.spinner.hidden = true;
        if (this.currentPage >= this.totalPages) {
          this.finish();
        } else if (this.mode === 'button') {
          if (this.button) this.button.disabled = false;
        } else if (this.observer) {
          // An IntersectionObserver only fires on transitions. If the new batch
          // didn't push the sentinel out of view (tall screen / short batch),
          // re-observing forces a fresh evaluation so the feed keeps loading.
          this.observer.unobserve(this.sentinel);
          this.observer.observe(this.sentinel);
        }
      }
    }

    finish() {
      if (this.observer) this.observer.disconnect();
      if (this.sentinel) this.sentinel.remove();
      if (this.button) this.button.hidden = true;
      // Only announce the end once the shopper has actually pulled in more
      // pages — no point showing it under a grid that was short to begin with.
      if (this.endEl && this.currentPage > this.startPage) this.endEl.hidden = false;
    }
  }

  customElements.define('mkt-infinite', MktInfinite);
}
